/**
 * <s-product-gallery> — product media carousel (one slide at a time).
 *
 * Markup (templates/product.liquid):
 *   [data-product-gallery]  scroll-snap track (swipe / trackpad / keyboard when focused)
 *   [data-gallery-slide]    one per media, carries data-media-id
 *   [data-gallery-prev] / [data-gallery-next]  arrow buttons (tablet + desktop)
 *   [data-gallery-thumbs] > [data-gallery-thumb][data-index]  thumbnail row (tablet + desktop)
 *   [data-gallery-current]  counter text ("1" in "1 / 8")
 *   data-initial-media-id   media to show on load: the selected (or first available) variant's own image
 *
 * Every movement scrolls the track or the thumbnail row only (element.scrollTo),
 * never the page. showMedia(mediaId) is the public API used on variant change:
 * it switches in place and does nothing when the variant has no image.
 */
class ProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #thumbRow;
  #prev;
  #next;
  #counter;
  #index = 0;
  #frame = 0;
  // Slide a programmatic move is heading to. While set, scroll events from that move (or from the
  // browser re-snapping while images load) cannot change the selection; a real swipe, wheel or key clears it.
  #target = null;
  #smoothMoving = false; // an arrow/thumbnail animation is in flight; its intermediate scroll events are expected

  connectedCallback() {
    this.#track = this.querySelector('[data-product-gallery]');
    if (!this.#track) return;
    this.#slides = [...this.#track.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.#thumbRow = this.querySelector('[data-gallery-thumbs]');
    this.#prev = this.querySelector('[data-gallery-prev]');
    this.#next = this.querySelector('[data-gallery-next]');
    this.#counter = this.querySelector('[data-gallery-current]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track.addEventListener('scroll', () => this.#onScroll(), { ...opts, passive: true });
    this.#prev?.addEventListener('click', () => this.go(this.#index - 1), opts);
    this.#next?.addEventListener('click', () => this.go(this.#index + 1), opts);
    for (const thumb of this.#thumbs) {
      thumb.addEventListener('click', () => this.go(Number(thumb.dataset.index)), opts);
    }
    this.#track.addEventListener('keydown', (event) => this.#onKey(event), opts);
    // Keep the visible slide aligned when the layout width changes (rotation, resize).
    new ResizeObserver(() => this.go(this.#index, { behavior: 'instant' })).observe(this.#track);
    this.#ac.signal.addEventListener('abort', () => cancelAnimationFrame(this.#frame));

    // Real user scrolling hands control back to scroll position.
    const release = () => { this.#target = null; };
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      this.#track.addEventListener(type, release, { ...opts, passive: true });
    }

    // Initial slide: the selected variant's own image (server-rendered in data-initial-media-id,
    // matching the counter and highlighted thumbnail), else slide 1.
    const initial = this.getAttribute('data-initial-media-id');
    const initialIndex = initial ? this.#indexOf(initial) : -1;
    if (initialIndex > 0) {
      this.go(initialIndex, { behavior: 'instant' });
      // Re-assert once layout and images settle, in case the browser re-snapped to slide 1 meanwhile.
      const reassert = () => { if (this.#target !== null) this.#align(this.#target); };
      requestAnimationFrame(reassert);
      if (document.readyState !== 'complete') window.addEventListener('load', reassert, { ...opts, once: true });
    } else {
      this.#update(0);
    }
  }

  /** Put the track exactly on a slide without animation. Scrolls only the track, never the page. */
  #align(index) {
    const slide = this.#slides[index];
    if (!slide) return;
    this.#track.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** Switch to the slide for a Shopify media id. Returns false when the id is not in this gallery. */
  showMedia(mediaId) {
    const index = this.#indexOf(mediaId);
    if (index < 0) return false;
    this.go(index, { behavior: 'instant' });
    return true;
  }

  go(index, { behavior } = {}) {
    if (!this.#slides.length) return;
    const target = Math.max(0, Math.min(this.#slides.length - 1, index));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const slide = this.#slides[target];
    const resolved = behavior ?? (reduceMotion ? 'instant' : 'smooth');
    this.#target = target;
    this.#smoothMoving = resolved === 'smooth';
    this.#track.scrollTo({ left: slide.offsetLeft, behavior: resolved });
    this.#update(target);
  }

  #indexOf(mediaId) {
    return this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
  }

  #onScroll() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.#track.clientWidth || 1;
      // Math.abs keeps RTL (negative scrollLeft) working.
      const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
      if (this.#target !== null) {
        // A programmatic move owns the selection: clear the hold once it arrives, and pull the
        // track back if something else (a re-snap while images load) moved it away.
        if (index === this.#target) this.#target = null;
        else if (!this.#smoothMoving) this.#align(this.#target);
        return;
      }
      if (index !== this.#index) this.#update(index);
    });
  }

  #onKey(event) {
    if (event.target !== this.#track) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    let next = null;
    if (event.key === forward) next = this.#index + 1;
    else if (event.key === back) next = this.#index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.#slides.length - 1;
    if (next === null) return;
    event.preventDefault();
    this.go(next);
  }

  #update(index) {
    this.#index = Math.max(0, Math.min(this.#slides.length - 1, index));
    if (this.#counter) this.#counter.textContent = String(this.#index + 1);
    if (this.#prev) this.#prev.disabled = this.#index === 0;
    if (this.#next) this.#next.disabled = this.#index === this.#slides.length - 1;

    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('inert', !active && this.#slides.length > 1);
      // Pause hosted videos that scroll out of view.
      if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
      // Prioritise the visible slide and its neighbours.
      if (Math.abs(i - this.#index) <= 1) {
        slide.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      }
    });

    this.#thumbs.forEach((thumb, i) => {
      if (i === this.#index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const activeThumb = this.#thumbs[this.#index];
    if (activeThumb && this.#thumbRow && this.#thumbRow.scrollWidth > this.#thumbRow.clientWidth) {
      const item = activeThumb.parentElement ?? activeThumb;
      const left = item.offsetLeft - (this.#thumbRow.clientWidth - item.offsetWidth) / 2;
      this.#thumbRow.scrollTo({ left: Math.max(0, left), behavior: 'instant' });
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', ProductGallery);
